import React, {useMemo, useState} from 'react';

import {makeStyles} from "@material-ui/core";
import StepLabel from "@material-ui/core/StepLabel";
import StepContent from "@material-ui/core/StepContent";
import Stepper from "@material-ui/core/Stepper";
import Button from "@material-ui/core/Button";
import Step from "@material-ui/core/Step";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import Radio from "@material-ui/core/Radio";
import FormHelperText from "@material-ui/core/FormHelperText";

import {CheckboxTree} from "../CheckboxTree/CheckboxTree";
import {UserRolesOptions} from "../../utils/roles";
import {makeTree} from "../../utils/treeUtils";
import {getContractsId, UserDialogModes} from "../../utils/users";


const useStyles = makeStyles(() => ({buttonBar: {marginTop: '16px'}}))

const FieldList = ({fields, touched, errors, getFieldProps}) => {
    return <Grid container direction={'column'}>
        {fields.map(field => <Grid key={`field-${field.id}`} item>
            <TextField {...getFieldProps(field.id)}
                       error={touched[field.id] && Boolean(errors[field.id])}
                       helperText={errors[field.id] && touched[field.id] ? errors[field.id] : undefined}
                       type={field?.type ? field?.type : undefined}
                       label={field.label}
                       required={field?.required ? field?.required : undefined}
                       fullWidth
            />
        </Grid>)}
    </Grid>
}


const PersonalInformation = ({mode, touched, errors, getFieldProps}) => {
    const fields = [
        {id: 'last_name', label: 'Фамилия', required: true},
        {id: 'first_name', label: 'Имя', required: true},
        {id: 'mid_name', label: 'Отчество'},
        {id: 'organization', label: 'Организация', required: true},
        {id: 'appointment', label: 'Должность', required: true},
        {id: 'phone_number', label: 'Номер телефона', type: 'tel'},
        {id: 'email', label: 'E-mail', type: 'email', required: true},
        {id: 'username', label: 'Логин', required: true},
    ]

    if (mode === UserDialogModes.CREATE) {
        fields.push(
            {id: 'password', label: 'Пароль', type: 'password', required: true},
            {id: 'confirm_password', label: 'Повторите пароль', type: 'password', required: true}
        )
    }

    return <FieldList fields={fields} touched={touched} errors={errors} getFieldProps={getFieldProps}/>
}

const FullNameAndAppointmentCases = ({touched, errors, getFieldProps}) => {
    const fields = [
        {id: 'full_name', label: 'ФИО кто'},
        {id: 'full_name_to_whom', label: 'ФИО кому'},
        {id: 'full_name_by_whom', label: 'ФИО кем'},
        {id: 'full_name_whom', label: 'ФИО кого'},
        {id: 'appointment_to_whom', label: 'Должность кому'},
        {id: 'appointment_by_whom', label: 'Должность кем'},
        {id: 'appointment_whom', label: 'Должность кого'}
    ]
    return <FieldList fields={fields} touched={touched} errors={errors} getFieldProps={getFieldProps}/>
}

const RoleSelect = ({getFieldProps, touched, errors}) => {
    const {value, onChange} = getFieldProps('role')
    return <FormControl component="fieldset" error={touched['role'] && Boolean(errors['role'])}>
        <RadioGroup value={value} name={'role'} onChange={onChange}>
            {UserRolesOptions.map(role => <FormControlLabel key={`field-role-${role.value}`}
                                                            control={<Radio/>} {...role} />)}
        </RadioGroup>
        <FormHelperText>{errors['role'] && touched['role'] ? errors['role'] : undefined}</FormHelperText>
    </FormControl>
}

const ContractsSelect = ({companies, contracts, getFieldProps, setFieldValue}) => {
    const [expanded, setExpanded] = useState([])
    const {value} = getFieldProps('contracts')
    const selectedCompaniesAndContracts = useMemo(() => value.map(el => `contract_${el}`), [value])

    const nodes = useMemo(() => {
        if (!companies || companies.length === 0) {
            return []
        }

        const companiesCopy = companies.map(el => ({
                ...el, label: el.name, value: `company_${el.id}`,
            })
        )
        const contractsCopy = contracts.map(el => ({
                ...el, label: el.name, value: `contract_${el.id}`,
            })
        )
        const treeConfig = [
            {
                selfParentKey: 'company',
                root: true,
                rootValue: null
            },
            {
                parentPosition: 0,
                parentKey: 'company',
                root: false,
            },
        ]
        return makeTree([companiesCopy, contractsCopy], treeConfig)
    }, [companies, contracts])

    return <CheckboxTree
        nodes={nodes}
        checked={selectedCompaniesAndContracts}
        expanded={expanded}
        onCheck={checked => setFieldValue('contracts', getContractsId(checked)['contracts'])}
        onExpand={expanded => setExpanded(expanded)}
        showNodeIcon={false}
    />
}


export const UserDialogContent = ({companies, contracts, mode, touched, errors, getFieldProps, setFieldValue, handleSubmit}) => {
    const classes = useStyles()

    const stepsNames = ['Личная информация', 'Склонение ФИО и должности', 'Выбор роли', 'Выбор проекта']

    const [activeStep, setActiveStep] = useState(0)

    const handleNext = () => setActiveStep(curr => curr + 1)

    const handleBack = () => setActiveStep(curr => curr - 1)

    const stepsContent = [
        <PersonalInformation mode={mode}
                             getFieldProps={getFieldProps}
                             touched={touched}
                             errors={errors}/>,
        <FullNameAndAppointmentCases mode={mode}
                                     getFieldProps={getFieldProps}
                                     touched={touched}
                                     errors={errors}/>,
        <RoleSelect mode={mode}
                    getFieldProps={getFieldProps}
                    touched={touched}
                    errors={errors}
        />,
        <ContractsSelect mode={mode}
                         contracts={contracts}
                         companies={companies}
                         getFieldProps={getFieldProps}
                         setFieldValue={setFieldValue}
        />
    ]

    return <Stepper activeStep={activeStep} orientation="vertical">
        {stepsNames.map((label, index) => (
            <Step key={label}>
                <StepLabel>{label}</StepLabel>
                <StepContent>
                    {stepsContent[index]}
                    <div className={classes.buttonBar}>
                        <div>
                            <Button
                                disabled={activeStep === 0}
                                onClick={handleBack}
                            >
                                Назад
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={activeStep === stepsNames.length - 1 ? handleSubmit : handleNext}
                            >
                                {activeStep === stepsNames.length - 1 ? mode === UserDialogModes.CREATE ? 'Создать' : 'Сохранить' : 'Далее'}
                            </Button>
                        </div>
                    </div>
                </StepContent>
            </Step>
        ))}
    </Stepper>
}